import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Check, Crown, Smartphone, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { API, toast } from '@/App';
import MobileMoneyPayment from '@/components/shared/MobileMoneyPayment';
import { logClientError } from '@/utils/clientError';
import { MOBILE_MONEY_METHOD_IDS } from '@/utils/paymentMethodAvailability';
import { uploadPaymentProof } from '@/utils/paymentProofUpload';
import { getPromoFeedbackMessage, normalizePromoCodeInput } from '@/utils/promoCodes';

const PLAN_RANK = {
  starter_1m: 1,
  pro_3m: 2,
  business_12m: 3,
};

const formatDateLabel = (value) => {
  if (!value) {
    return '-';
  }
  try {
    return new Date(value).toLocaleDateString('fr-FR');
  } catch (error) {
    return '-';
  }
};

const SubscribeExpress = () => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [currency, setCurrency] = useState('FCFA');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [walletSummary, setWalletSummary] = useState(null);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoValidation, setPromoValidation] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [pricingPreviewLoading, setPricingPreviewLoading] = useState(false);
  const [autoRenewSubmitting, setAutoRenewSubmitting] = useState(false);
  const [showPlanChangeDialog, setShowPlanChangeDialog] = useState(false);
  const [planChangeTarget, setPlanChangeTarget] = useState('');
  const [planChangePreview, setPlanChangePreview] = useState(null);
  const [planChangePreviewLoading, setPlanChangePreviewLoading] = useState(false);
  const [planChangeProcessing, setPlanChangeProcessing] = useState(false);

  useEffect(() => {
    loadUserData();
    loadPlans();
  }, []);

  const loadUserData = async () => {
    try {
      const [userResponse, subResponse, walletResponse] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/subscriptions/my-subscription`),
        axios.get(`${API}/wallet`).catch(() => ({ data: null })),
      ]);

      setUser(userResponse.data);
      setCurrency(userResponse.data?.city === 'KIN' ? 'USD' : 'FCFA');
      setSubscription(subResponse.data);
      setWalletSummary(walletResponse.data || null);
    } catch (error) {
      logClientError('subscriptions.load_user_data', error);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/pricing`);
      setPlans(response.data || []);
    } catch (error) {
      logClientError('subscriptions.load_pricing', error);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setPaymentMethod('');
    setTransactionId('');
    setPaymentProofFile(null);
    setPromoCodeInput('');
    setPromoValidation(null);
    setShowPaymentDialog(true);
  };

  const fetchSubscriptionPricingPreview = async (planCode, promoCode = '') => {
    const response = await axios.post(`${API}/subscriptions/pricing-preview`, {
      plan: planCode,
      promo_code: normalizePromoCodeInput(promoCode) || undefined,
    });
    return response.data || {};
  };

  const loadSubscriptionPricingPreview = async (planCode, promoCode = '', { notify = false } = {}) => {
    if (!planCode) {
      return null;
    }
    setPricingPreviewLoading(true);
    try {
      const payload = await fetchSubscriptionPricingPreview(planCode, promoCode);
      setPromoValidation(payload);
      if (notify && payload.message) {
        toast.success(payload.message);
      }
      return payload;
    } catch (error) {
      if (notify) {
        toast.error(getPromoFeedbackMessage(error.response?.data?.detail, 'Impossible de recalculer ce montant.'));
      }
      return null;
    } finally {
      setPricingPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!showPaymentDialog || !selectedPlan?.plan) {
      return;
    }
    loadSubscriptionPricingPreview(selectedPlan.plan);
  }, [showPaymentDialog, selectedPlan?.plan]);

  const activeSubscription = subscription?.subscription || null;
  const latestSubscription = subscription?.latest_subscription || null;
  const subscriptionManagement = subscription?.subscription_management || {};
  const currentPlanCode = subscriptionManagement.current_plan_code || activeSubscription?.plan || subscription?.plan || null;
  const currentPlanLabel = subscriptionManagement.current_plan_label || plans.find((item) => item.plan === currentPlanCode)?.name || '-';
  const autoRenewEnabled = Boolean(subscriptionManagement.auto_renew_enabled);
  const autoRenewStatus = subscriptionManagement.auto_renew_status || null;
  const autoRenewNextChargeAt = subscriptionManagement.auto_renew_next_charge_at || activeSubscription?.end_date || subscription?.expires_at || null;
  const autoRenewAmount = Number(subscriptionManagement.auto_renew_amount || 0);
  const autoRenewCurrency = subscriptionManagement.auto_renew_currency || currency;
  const pendingPlanCode = subscriptionManagement.pending_plan_code || null;
  const pendingPlanLabel = subscriptionManagement.pending_plan_label || plans.find((item) => item.plan === pendingPlanCode)?.name || null;
  const availablePlanChanges = useMemo(
    () => plans.filter((item) => item.plan !== currentPlanCode),
    [plans, currentPlanCode],
  );
  const selectedChangePlan = useMemo(
    () => plans.find((item) => item.plan === planChangeTarget) || null,
    [plans, planChangeTarget],
  );
  const planChangeEffect = useMemo(() => {
    if (!currentPlanCode || !planChangeTarget) {
      return null;
    }
    return (PLAN_RANK[planChangeTarget] || 0) > (PLAN_RANK[currentPlanCode] || 0) ? 'immediate' : 'at_expiration';
  }, [currentPlanCode, planChangeTarget]);
  const planChangeAmount = Number(planChangePreview?.pricing?.amount_after ?? 0);
  const planChangeCurrency = planChangePreview?.pricing?.currency || currency;

  useEffect(() => {
    if (!showPlanChangeDialog) {
      return;
    }
    if (!planChangeTarget) {
      const fallbackPlan = availablePlanChanges[0]?.plan || '';
      if (fallbackPlan) {
        setPlanChangeTarget(fallbackPlan);
      }
      return;
    }
    let isMounted = true;
    const loadPreview = async () => {
      setPlanChangePreviewLoading(true);
      try {
        const payload = await fetchSubscriptionPricingPreview(planChangeTarget);
        if (isMounted) {
          setPlanChangePreview(payload);
        }
      } catch (error) {
        if (isMounted) {
          setPlanChangePreview(null);
          toast.error(getPromoFeedbackMessage(error.response?.data?.detail, 'Impossible de calculer ce changement de plan.'));
        }
      } finally {
        if (isMounted) {
          setPlanChangePreviewLoading(false);
        }
      }
    };
    loadPreview();
    return () => {
      isMounted = false;
    };
  }, [availablePlanChanges, planChangeTarget, showPlanChangeDialog]);

  const selectedPlanAmount = useMemo(() => {
    if (!selectedPlan) {
      return 0;
    }
    return Number(currency === 'FCFA' ? selectedPlan.price_fcfa : selectedPlan.price_usd || 0);
  }, [currency, selectedPlan]);

  const selectedPlanPricing = useMemo(() => {
    const pricing = promoValidation?.pricing || {};
    const amountBefore = Number(pricing.amount_before ?? selectedPlanAmount ?? 0);
    const discountAmount = Number(pricing.discount_amount ?? pricing.total_discount_amount ?? 0);
    const referralDiscountAmount = Number(pricing.referral_discount_amount ?? 0);
    const promoDiscountAmount = Number(
      pricing.promo_discount_amount ?? Math.max(0, discountAmount - referralDiscountAmount)
    );
    const amountAfter = Number(pricing.amount_after ?? selectedPlanAmount ?? 0);
    return {
      amountBefore,
      discountAmount,
      referralDiscountAmount,
      referralDiscountPercent: Number(pricing.referral_discount_percent ?? 0),
      hasReferralDiscount: Boolean(pricing.has_referral_discount || referralDiscountAmount > 0),
      promoDiscountAmount,
      hasPromoDiscount: Boolean(pricing.has_promo_discount || promoDiscountAmount > 0),
      amountAfter,
      hasDiscount: Boolean(pricing.has_discount || discountAmount > 0),
      currency: pricing.currency || currency,
      commissionBaseAmount: Number(pricing.commission_base_amount ?? amountAfter),
      promoCode: promoValidation?.promo_code?.code || promoValidation?.promo_code?.requested_code || null,
      promoLabel: promoValidation?.promo_code?.label || promoValidation?.promo_code?.code || promoValidation?.promo_code?.requested_code || null,
      promoApplied: Boolean(promoValidation?.promo_code?.applied),
    };
  }, [currency, promoValidation, selectedPlanAmount]);

  const handleApplyPromoCode = async () => {
    const normalizedPromoCode = normalizePromoCodeInput(promoCodeInput);
    if (!normalizedPromoCode) {
      toast.error('Saisissez un code promo.');
      return;
    }
    if (!selectedPlan?.plan) {
      toast.error('Selectionnez une formule avant d appliquer un code promo.');
      return;
    }

    setPromoLoading(true);
    try {
      const result = await loadSubscriptionPricingPreview(selectedPlan.plan, normalizedPromoCode, { notify: false });
      if (!result) {
        return;
      }
      if (!result.valid) {
        setPromoValidation(result);
        toast.error(getPromoFeedbackMessage(result.status, result.message));
        return;
      }
      setPromoCodeInput(normalizedPromoCode);
      toast.success(result.message || 'Montant mis a jour.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (processing) {
      return;
    }
    if (!paymentMethod) {
      toast.error('Veuillez selectionner une methode de paiement');
      return;
    }
    if (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod) && !paymentProofFile) {
      toast.error('Veuillez ajouter une preuve de paiement Mobile Money');
      return;
    }

    setProcessing(true);
    try {
      let proofFileId;
      if (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod)) {
        const uploadedProof = await uploadPaymentProof({
          file: paymentProofFile,
          context: 'subscription',
        });
        proofFileId = uploadedProof?.id;
      }

      const response = await axios.post(`${API}/subscriptions/subscribe`, null, {
        params: {
          plan: selectedPlan.plan,
          payment_method: paymentMethod,
          transaction_id: transactionId.trim() || undefined,
          proof_file_id: proofFileId,
          promo_code: promoValidation?.promo_code?.applied
            ? normalizePromoCodeInput(
              promoValidation?.promo_code?.code
              || promoValidation?.promo_code?.requested_code
              || promoCodeInput
            )
            : undefined,
        },
      });
      const payload = response.data || {};

      if (payload.status === 'active') {
        toast.success(
          payload.already_processed
            ? 'Votre abonnement Pro est deja actif.'
            : (payload.message || 'Abonnement active avec succes.')
        );
      } else {
        toast.success(payload.message || 'Demande d abonnement envoyee. En attente de validation admin.');
      }

      setShowPaymentDialog(false);
      setPaymentMethod('');
      setTransactionId('');
      setPaymentProofFile(null);
      setPromoCodeInput('');
      setPromoValidation(null);
      await loadUserData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'INSUFFICIENT_BALANCE') {
        toast.error('Solde du portefeuille insuffisant pour payer cet abonnement.');
      } else if (detail === 'SUBSCRIPTION_PAYMENT_ALREADY_PROCESSING') {
        toast.error('Le paiement portefeuille de cet abonnement est deja en cours. Actualisez la page avant de recommencer.');
      } else {
        toast.error(getPromoFeedbackMessage(detail, detail || 'Erreur lors de l abonnement'));
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleToggleAutoRenew = async (enabled) => {
    if (!activeSubscription || autoRenewSubmitting) {
      return;
    }
    setAutoRenewSubmitting(true);
    try {
      const response = await axios.patch(`${API}/subscriptions/auto-renew`, {
        enabled,
        plan_code: enabled ? (pendingPlanCode || currentPlanCode || undefined) : undefined,
      });
      const payload = response.data || {};
      toast.success(payload.message || (enabled ? 'Renouvellement automatique active.' : 'Renouvellement automatique desactive.'));
      await loadUserData();
    } catch (error) {
      toast.error(getPromoFeedbackMessage(error.response?.data?.detail, 'Impossible de mettre a jour le renouvellement automatique.'));
    } finally {
      setAutoRenewSubmitting(false);
    }
  };

  const openPlanChangeDialog = () => {
    const fallbackPlan = availablePlanChanges[0]?.plan || '';
    setPlanChangeTarget(fallbackPlan);
    setPlanChangePreview(null);
    setShowPlanChangeDialog(true);
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeTarget || planChangeProcessing) {
      return;
    }
    setPlanChangeProcessing(true);
    try {
      const response = await axios.post(`${API}/subscriptions/change-plan`, {
        plan: planChangeTarget,
      });
      const payload = response.data || {};
      if (payload.effect === 'immediate') {
        toast.success(payload.message || 'Le nouveau plan a ete active immediatement.');
      } else {
        toast.success(payload.message || 'Le changement de plan prendra effet a l expiration de votre abonnement.');
      }
      setShowPlanChangeDialog(false);
      setPlanChangePreview(null);
      await loadUserData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'INSUFFICIENT_BALANCE') {
        toast.error('Solde du portefeuille insuffisant pour changer de plan immediatement.');
      } else {
        toast.error(getPromoFeedbackMessage(detail, detail || 'Impossible de changer de plan.'));
      }
    } finally {
      setPlanChangeProcessing(false);
    }
  };

  if (!user) {
    return <div className="flex items-center justify-center p-12"><div className="spinner"></div></div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <Card className="bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0">
        <CardContent className="p-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
              <Crown className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-4xl font-bold mb-2">Coli Noki Pro</h1>
              <p className="text-xl text-amber-50">Priorite maximale pour vos livraisons</p>
            </div>
          </div>

          {subscription?.is_express && (
            <div className="mt-6 p-4 bg-white/20 backdrop-blur-sm rounded-lg">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-lg">Votre abonnement Pro est actif</p>
                  <p className="text-amber-50 text-sm mt-1">
                    Plan actuel : {currentPlanLabel}
                  </p>
                  <p className="text-amber-50 text-sm mt-1">
                    Expire le : {formatDateLabel(subscription.expires_at)}
                  </p>
                  {pendingPlanLabel && (
                    <p className="text-amber-50 text-sm mt-1">
                      Prochain plan programme : {pendingPlanLabel}
                    </p>
                  )}
                </div>
                <Badge className="bg-green-500 text-white text-lg px-4 py-2">ACTIF</Badge>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!subscription?.is_express && latestSubscription && autoRenewStatus === 'failed' && (
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-5">
            <p className="font-semibold text-amber-900">Renouvellement automatique echoue</p>
            <p className="mt-1 text-sm text-amber-800">
              Le dernier prelevement automatique via votre portefeuille n a pas abouti. Rechargez votre portefeuille, puis relancez un abonnement Pro.
            </p>
            {subscriptionManagement.auto_renew_last_attempt_at && (
              <p className="mt-2 text-xs text-amber-700">
                Derniere tentative : {formatDateLabel(subscriptionManagement.auto_renew_last_attempt_at)}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {subscription?.is_express && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-cyan-200">
            <CardHeader>
              <CardTitle>Renouvellement automatique</CardTitle>
              <CardDescription>
                Votre abonnement sera renouvelle automatiquement via votre portefeuille si le solde est suffisant.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <div>
                  <p className="font-medium">{autoRenewEnabled ? 'Active' : 'Desactive'}</p>
                  <p className="text-sm text-slate-500">
                    {autoRenewEnabled ? 'Le portefeuille sera tente a la prochaine echeance.' : 'Aucun prelevement automatique ne sera tente.'}
                  </p>
                </div>
                <Switch
                  checked={autoRenewEnabled}
                  disabled={autoRenewSubmitting}
                  onCheckedChange={handleToggleAutoRenew}
                  data-testid="subscription-auto-renew-switch"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-slate-500">Statut</p>
                  <p className="mt-1 font-semibold capitalize">{autoRenewStatus || 'inactive'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-slate-500">Prochain prelevement</p>
                  <p className="mt-1 font-semibold">{formatDateLabel(autoRenewNextChargeAt)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-slate-500">Montant prevu</p>
                  <p className="mt-1 font-semibold">
                    {autoRenewAmount > 0 ? `${autoRenewAmount.toLocaleString()} ${autoRenewCurrency}` : '-'}
                  </p>
                </div>
              </div>

              {pendingPlanLabel && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Le prochain renouvellement utilisera le plan {pendingPlanLabel}.
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Changer de plan</CardTitle>
              <CardDescription>
                Passez a une offre superieure immediatement ou programmez un downgrade a la fin de la periode en cours.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-sm text-slate-500">Plan actuel</p>
                <p className="mt-1 text-lg font-semibold">{currentPlanLabel}</p>
                <p className="mt-1 text-sm text-slate-500">Expiration : {formatDateLabel(subscription.expires_at)}</p>
              </div>

              {pendingPlanLabel && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  Un changement vers {pendingPlanLabel} est deja programme pour l expiration de votre abonnement.
                </div>
              )}

              <Button
                type="button"
                variant="outline"
                onClick={openPlanChangeDialog}
                disabled={planChangeProcessing || availablePlanChanges.length === 0}
                data-testid="open-plan-change-dialog-btn"
              >
                Changer de plan
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex items-center justify-center gap-4">
        <span className="text-sm font-medium">Afficher les prix en :</span>
        <div className="flex bg-gray-200 rounded-lg p-1">
          <Button
            variant={currency === 'FCFA' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCurrency('FCFA')}
            className={currency === 'FCFA' ? 'bg-red-500 hover:bg-red-600' : ''}
          >
            FCFA
          </Button>
          <Button
            variant={currency === 'USD' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setCurrency('USD')}
            className={currency === 'USD' ? 'bg-red-500 hover:bg-red-600' : ''}
          >
            USD
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card
            key={plan.plan}
            className={`relative overflow-hidden transition-all hover:shadow-xl ${plan.is_popular ? 'border-4 border-amber-500 shadow-lg' : ''}`}
          >
            {plan.is_popular && (
              <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 text-sm font-semibold">
                POPULAIRE
              </div>
            )}

            <CardHeader className="pt-8">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center mb-4">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="text-2xl">{plan.name}</CardTitle>
              <CardDescription className="text-base mt-2">{plan.description}</CardDescription>
            </CardHeader>

            <CardContent>
              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">{currency === 'FCFA' ? plan.price_fcfa : plan.price_usd}</span>
                  <span className="text-xl text-gray-600">{currency}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">Pour {plan.duration_months} mois</p>
              </div>

              <div className="space-y-3 mb-6">
                {[
                  'Priorite elevee sur toutes vos commandes',
                  'Attribution rapide des livreurs',
                  'Badge Pro visible',
                  'Support prioritaire',
                ].map((label) => (
                  <div key={label} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{label}</span>
                  </div>
                ))}
              </div>

              <Button
                data-testid={`subscribe-${plan.plan}`}
                className={`w-full ${plan.is_popular ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' : 'bg-red-500 hover:bg-red-600'}`}
                onClick={() => handleSelectPlan(plan)}
                disabled={subscription?.is_express}
              >
                {subscription?.is_express ? 'Deja abonne' : 'S abonner'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showPlanChangeDialog} onOpenChange={setShowPlanChangeDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Changer de plan</DialogTitle>
            <DialogDescription>
              Comparez votre plan actuel avec la nouvelle formule avant validation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3">
              <Label htmlFor="subscription-plan-change">Nouveau plan</Label>
              <select
                id="subscription-plan-change"
                className="h-11 rounded-md border border-slate-300 px-3 text-sm"
                value={planChangeTarget}
                onChange={(event) => setPlanChangeTarget(event.target.value)}
                data-testid="subscription-plan-change-select"
              >
                {availablePlanChanges.map((plan) => (
                  <option key={plan.plan} value={plan.plan}>
                    {plan.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-slate-200">
                <CardContent className="p-4 space-y-2">
                  <p className="text-sm text-slate-500">Plan actuel</p>
                  <p className="font-semibold">{currentPlanLabel}</p>
                  <p className="text-sm text-slate-500">Expiration actuelle : {formatDateLabel(subscription?.expires_at)}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4 space-y-2">
                  <p className="text-sm text-slate-500">Nouveau plan</p>
                  <p className="font-semibold">{selectedChangePlan?.name || '-'}</p>
                  <p className="text-sm text-slate-500">
                    {planChangeEffect === 'immediate'
                      ? 'Effet immediat apres validation.'
                      : `Effet a l expiration (${formatDateLabel(subscription?.expires_at)})`}
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="rounded-lg border border-dashed border-slate-300 p-4 text-sm space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span>Date d effet</span>
                <span className="font-medium">
                  {planChangeEffect === 'immediate'
                    ? 'Immediat'
                    : formatDateLabel(subscription?.expires_at)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span>Montant</span>
                <span className="font-medium">
                  {planChangeEffect === 'immediate'
                    ? (planChangePreviewLoading ? 'Calcul...' : `${planChangeAmount.toLocaleString()} ${planChangeCurrency}`)
                    : (planChangePreviewLoading ? 'Calcul...' : `${planChangeAmount.toLocaleString()} ${planChangeCurrency} au prochain renouvellement`)}
                </span>
              </div>
              {planChangeEffect === 'immediate' ? (
                <p className="text-xs text-slate-500">
                  L upgrade est preleve immediatement via votre portefeuille vendeur.
                </p>
              ) : (
                <p className="text-xs text-slate-500">
                  Le downgrade est programme et n entraine aucun debit immediat.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowPlanChangeDialog(false)}>
              Annuler
            </Button>
            <Button
              type="button"
              onClick={handleConfirmPlanChange}
              disabled={!planChangeTarget || planChangeProcessing || planChangePreviewLoading}
              data-testid="confirm-plan-change-btn"
            >
              {planChangeProcessing ? 'Traitement...' : 'Valider le changement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl">Finaliser l abonnement</DialogTitle>
              <DialogDescription className="text-base">
                {selectedPlan?.name} - {pricingPreviewLoading ? 'Calcul...' : `${selectedPlanPricing.amountAfter.toLocaleString()} ${selectedPlanPricing.currency}`}
              </DialogDescription>
            </DialogHeader>

            <Card className="border border-dashed border-gray-300">
              <CardContent className="space-y-3 p-4">
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label htmlFor="subscription-promo-code">Code promo</Label>
                    <Input
                      id="subscription-promo-code"
                      data-testid="subscription-promo-input"
                      value={promoCodeInput}
                      onChange={(event) => setPromoCodeInput(event.target.value.toUpperCase())}
                      placeholder="PROMO20"
                    />
                  </div>
                    <Button
                      type="button"
                      data-testid="apply-subscription-promo-btn"
                      variant="outline"
                      onClick={handleApplyPromoCode}
                      disabled={promoLoading || pricingPreviewLoading || !selectedPlan}
                    >
                      {promoLoading ? 'Application...' : 'Appliquer'}
                    </Button>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Montant brut</span>
                    <span>{selectedPlanPricing.amountBefore.toLocaleString()} {selectedPlanPricing.currency}</span>
                  </div>
                  {selectedPlanPricing.hasReferralDiscount && (
                    <div className="flex items-center justify-between text-emerald-700">
                      <span>Remise parrainage{selectedPlanPricing.referralDiscountPercent > 0 ? ` (${selectedPlanPricing.referralDiscountPercent}%)` : ''}</span>
                      <span>- {selectedPlanPricing.referralDiscountAmount.toLocaleString()} {selectedPlanPricing.currency}</span>
                    </div>
                  )}
                  {selectedPlanPricing.hasPromoDiscount && (
                    <div className="flex items-center justify-between text-green-700">
                      <span>Code promo{selectedPlanPricing.promoCode ? ` (${selectedPlanPricing.promoCode})` : ''}</span>
                      <span>- {selectedPlanPricing.promoDiscountAmount.toLocaleString()} {selectedPlanPricing.currency}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t pt-2 font-semibold">
                    <span>Montant net attendu</span>
                    <span>{selectedPlanPricing.amountAfter.toLocaleString()} {selectedPlanPricing.currency}</span>
                  </div>
                  {(promoValidation?.message || selectedPlanPricing.hasReferralDiscount) && (
                    <p className="pt-1 text-xs text-slate-500">
                      {promoValidation?.message || 'La remise parrainage est calculee par le backend sur votre premier abonnement Pro eligible.'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Tabs defaultValue="mobile-money" className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="mobile-money" className="flex items-center gap-2">
                <Smartphone className="w-4 h-4" />
                Mobile Money
              </TabsTrigger>
              <TabsTrigger value="wallet" className="flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Portefeuille
              </TabsTrigger>
            </TabsList>

              <TabsContent value="mobile-money" className="mt-6">
                <MobileMoneyPayment
                  amount={selectedPlanPricing.amountAfter}
                  currency={selectedPlanPricing.currency}
                  selectedMethod={paymentMethod}
                  transactionId={transactionId}
                  receiptFile={paymentProofFile}
                onTransactionIdChange={setTransactionId}
                onReceiptFileChange={setPaymentProofFile}
                onPaymentMethodSelect={(method) => {
                  setPaymentMethod(method);
                  setTransactionId('');
                  setPaymentProofFile(null);
                }}
              />
            </TabsContent>

            <TabsContent value="wallet" className="mt-6">
              <Card className="border-2 border-cyan-200 bg-cyan-50">
                <CardContent className="p-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <Wallet className="w-8 h-8 text-cyan-600" />
                    <div>
                      <h3 className="font-semibold text-lg">Paiement via le portefeuille</h3>
                      <p className="text-sm text-gray-600">Activation immediate si votre solde est suffisant</p>
                    </div>
                  </div>

                  <div className="rounded border border-cyan-200 bg-white p-3 text-sm text-gray-700 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                      <span>Solde disponible</span>
                      <span className="font-semibold">
                        {Number(walletSummary?.balance || 0).toLocaleString()} {walletSummary?.currency || selectedPlanPricing.currency}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span>Montant de l abonnement</span>
                      <span className="font-semibold">{selectedPlanPricing.amountAfter.toLocaleString()} {selectedPlanPricing.currency}</span>
                    </div>
                  </div>

                  {Number(walletSummary?.balance || 0) < selectedPlanPricing.amountAfter && (
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                      Solde insuffisant pour cet abonnement. Rechargez votre portefeuille ou utilisez Mobile Money.
                    </div>
                  )}

                  <Button
                    type="button"
                    variant={paymentMethod === 'wallet' ? 'default' : 'outline'}
                    className={paymentMethod === 'wallet' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
                    onClick={() => {
                      setPaymentMethod('wallet');
                      setTransactionId('');
                      setPaymentProofFile(null);
                    }}
                  >
                    Utiliser mon portefeuille
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Annuler
            </Button>
            <Button
              data-testid="confirm-subscription-btn"
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleSubscribe}
              disabled={processing || !paymentMethod || (MOBILE_MONEY_METHOD_IDS.includes(paymentMethod) && !paymentProofFile)}
            >
              {processing ? (
                <>
                  <div className="spinner mr-2"></div>
                  Traitement...
                </>
              ) : (
                'Confirmer l abonnement'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscribeExpress;
